import { KeyboardArrowDown, Search } from '@mui/icons-material';
import { Checkbox, FormControlLabel, InputAdornment, TextField, Typography } from '@mui/material';
import Badge from '@mui/material/Badge';
import Button from '@mui/material/Button';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { Box } from '@mui/system';
import { ChangeEvent, useEffect, useState, type FC } from 'react';
import { usePopover } from 'src/hooks/use-popover';

interface MultiSelectProps {
  label: string;
  onChange?: any;
  options: { label: string; id: string }[];
  selectedValue?: any;
  setTherapistListPageNo: any;
  appliedvalues?: any;
  currentTab?: string;
}

export const MultiSelectPatientList: FC<MultiSelectProps> = (props: any) => {
  const {
    label,
    onChange,
    options,
    currentTab,
    selectedValue,
    setTherapistListPageNo,
    appliedvalues,
    ...other
  } = props;
  const popover = usePopover<HTMLButtonElement>();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedValues, setSelectedValues] = useState<any>(selectedValue);

  useEffect(() => {
    if (popover.open) {
      setSelectedValues(selectedValue);
    }
  }, [popover.open, selectedValue]);

  const handleValueChange = (event: ChangeEvent<HTMLInputElement>, option: any): void => {
    const { value: checkedValue, checked } = event.target;
    let updatedSelectedValues = [...selectedValues];
    if (checked) {
      if (!updatedSelectedValues.find((item) => item === checkedValue)) {
        updatedSelectedValues.push(option);
      }
    } else {
      updatedSelectedValues = updatedSelectedValues.filter((item) => item !== checkedValue);
    }

    setSelectedValues(updatedSelectedValues);
  };

  const filteredOptions = options?.filter((option: any) => {
    const normalizedLabel = option?.label?.replace(/[\s,]/g, '')?.toLowerCase();
    const normalizedSearchTerm = searchTerm?.replace(/[\s,]/g, '')?.toLowerCase();
    return normalizedLabel?.includes(normalizedSearchTerm);
  });

  const handleApply = () => {
    onChange(selectedValues);
    popover.handleClose();
  };

  const handleClear = () => {
    setSelectedValues([]);
  };

  return (
    <>
      <Badge
        color="primary"
        badgeContent={appliedvalues}
        sx={{
          right: -5,
        }}
      >
        <Button
          sx={{
            color: '#111927',
            fontWeight: '600',
          }}
          endIcon={<KeyboardArrowDown />}
          onClick={popover.handleOpen}
          ref={popover.anchorRef}
          {...other}
        >
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: '13px',
              color: '#6C737F',
            }}
            // noWrap={true}
          >
            {label}
          </Typography>
        </Button>
      </Badge>
      <Menu
        anchorEl={popover.anchorRef.current}
        MenuListProps={{
          style: {
            width: '250px',
            maxHeight: '400px',
            padding: '5px',
            overflow: 'hidden',
          },
        }}
        onClose={popover.handleClose}
        open={popover.open}
      >
        <MenuItem
          onClick={handleClear}
          disabled={!selectedValues?.length}
          sx={{ my: 0.5 }}
        >
          Clear filter
        </MenuItem>{' '}
        <TextField
          variant="outlined"
          placeholder={`Search by therapist`}
          onChange={(event: any) => {
            setSearchTerm(event.target.value);
          }}
          value={searchTerm}
          sx={{
            '& .MuiInputBase-input': {
              padding: '10px 0px',
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
        />
        <Box
          sx={{
            maxHeight: '240px',
            overflow: 'scroll',
          }}
        >
          {filteredOptions.map((option: any) => (
            <Box
              key={option.id}
              sx={{ px: '10px', py: '2px' }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={selectedValues.some((item: any) => item === option.id)}
                    onChange={(event) => handleValueChange(event, option.id)}
                    value={option.id}
                    sx={{
                      padding: '10px',
                    }}
                  />
                }
                label={option.label}
                sx={{
                  width: '100%',
                }}
              />
            </Box>
          ))}
          {filteredOptions?.length === 0 && (
            <MenuItem
              key={0}
              sx={{
                background: 'white',
                gap: '1rem',
                textAlign: 'center',
              }}
            >
              <Typography sx={{ textAlign: 'center', marginLeft: '4px' }}>
                No therapist available
              </Typography>
            </MenuItem>
          )}
        </Box>
        <Box
          p={1.5}
          pb={0.5}
          sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}
        >
          <Button
            variant="outlined"
            size="small"
            onClick={() => {
              popover.handleClose();
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            size="small"
            onClick={handleApply}
          >
            Apply
          </Button>
        </Box>
      </Menu>
    </>
  );
};
